import { useQuery } from "@tanstack/react-query";

import { supabase } from "~/store/initializeStore";
import { escapePostgrestPattern } from "../salesCrmUtils";
import { type LinkableOrder } from "../salesCrmTypes";
import {
  salesMeetingBundleSelectFields,
  type SalesMeetingBundle,
  type SalesMeetingRow,
} from "./useSalesMeetingsQuery";

export type BundleOptions = {
  bundles: SalesMeetingBundle[];
  orders: LinkableOrder[];
};

export const useBundleOptionsQuery = (activeMeeting: SalesMeetingRow | null, bundleSearch: string) =>
  useQuery({
    queryKey: [
      "salesCrmBundles",
      activeMeeting?.id,
      activeMeeting?.clientEmail,
      activeMeeting?.customerBundleId,
      activeMeeting?.matchedCustomerBundleId,
      bundleSearch,
    ],
    enabled: Boolean(activeMeeting?.clientEmail),
    retry: false,
    queryFn: async (): Promise<BundleOptions> => {
      const search = escapePostgrestPattern(bundleSearch);
      // Raw (structural chars stripped) for exact/array comparisons, escaped
      // variant for ilike patterns.
      const rawClientEmail = (activeMeeting?.clientEmail ?? "")
        .replace(/[,(){}]/g, "")
        .trim()
        .toLowerCase();
      const clientEmail = escapePostgrestPattern(activeMeeting?.clientEmail ?? "");
      const selectedBundleIds = [activeMeeting?.customerBundleId, activeMeeting?.matchedCustomerBundleId].filter(
        Boolean,
      ) as string[];
      // Resolve customer email aliases (wallet checkouts pay under a different
      // address), so the default suggestions cover every known address.
      let aliasEmails = rawClientEmail ? [rawClientEmail] : [];

      if (!search && rawClientEmail) {
        const { data: aliasRows } = await supabase
          .from("customer")
          .select("email, emailAliases")
          .or(`email.ilike.${clientEmail},emailAliases.cs.{${rawClientEmail}}`);

        for (const row of aliasRows ?? []) {
          if (row.email) {
            aliasEmails.push(row.email.toLowerCase());
          }
          for (const alias of row.emailAliases ?? []) {
            aliasEmails.push(alias.toLowerCase());
          }
        }

        aliasEmails = Array.from(new Set(aliasEmails));
      }

      const aliasOrFilter = aliasEmails.map((email) => `clientEmail.ilike.${escapePostgrestPattern(email)}`).join(",");

      let query = supabase
        .from("customer_bundle")
        .select(salesMeetingBundleSelectFields)
        .order("createdAt", { ascending: false })
        .limit(50);

      if (search) {
        query = query.or(`clientEmail.ilike.%${search}%,name.ilike.%${search}%,initialOrderId.ilike.%${search}%`);
      } else if (selectedBundleIds.length > 0 && aliasOrFilter) {
        query = query.or(`${aliasOrFilter},id.in.(${selectedBundleIds.join(",")})`);
      } else if (aliasOrFilter) {
        query = query.or(aliasOrFilter);
      }

      const { data, error } = await query;

      if (error) throw new Error(error.message);
      const bundles = (data ?? []) as SalesMeetingBundle[];

      // Free/manual orders never get a customer_bundle, so offer them for
      // manual linking — a bundle is created for the picked order on save.
      const ordersFilter = search ? `clientEmail.ilike.%${search}%` : aliasOrFilter;
      let orders: LinkableOrder[] = [];

      if (ordersFilter) {
        const { data: orderRows, error: ordersError } = await supabase
          .from("product_order")
          .select("id, createdAt, clientEmail, status, paymentType, product, price, items")
          .in("status", ["PAID", "FREE_FINALIZED"])
          .or(ordersFilter)
          .order("createdAt", { ascending: false })
          .limit(20);

        if (!ordersError) {
          const bundledOrderIds = new Set(bundles.map((bundle) => bundle.initialOrderId).filter(Boolean));
          const orderIds = (orderRows ?? []).map((row) => row.id).filter((id) => !bundledOrderIds.has(id));

          let alreadyBundled = new Set<string>();
          if (orderIds.length) {
            const { data: existingBundles } = await supabase
              .from("customer_bundle")
              .select("initialOrderId")
              .in("initialOrderId", orderIds);
            alreadyBundled = new Set(
              (existingBundles ?? []).map((row) => row.initialOrderId).filter(Boolean) as string[],
            );
          }

          orders = (orderRows ?? [])
            .filter((row) => !bundledOrderIds.has(row.id) && !alreadyBundled.has(row.id))
            .map((row) => {
              const price = (row.price ?? null) as {
                amount?: number;
                currency?: string;
                productName?: string;
              } | null;
              type OrderItem = {
                productName?: string | null;
                priceAmount?: number | null;
                installments?: number | null;
              };
              let items: OrderItem[] | null = null;
              try {
                items = (typeof row.items === "string" ? JSON.parse(row.items) : row.items) as OrderItem[] | null;
              } catch {
                items = null;
              }

              return {
                id: row.id,
                createdAt: row.createdAt,
                clientEmail: row.clientEmail,
                status: String(row.status),
                paymentType: row.paymentType,
                product: row.product,
                amount: price?.amount ?? items?.[0]?.priceAmount ?? null,
                currency: price?.currency ?? "pln",
                productName: price?.productName ?? items?.[0]?.productName ?? null,
                cycles: items?.[0]?.installments ?? null,
              };
            });
        }
      }

      return { bundles, orders };
    },
  });
